import createDebug from 'debug';
import { VesData } from '../VesData';
import { ApiHelper } from './ApiHelper';
import { DurableRecording } from '@/constants';

const debug = createDebug('ves:ApiHelper');

/**
 * @param {...*} args
 */
const logError = (...args) => {
  console.error('VesApiHelper:', ...args);
};

/**
 * Allows for making requests to the VES API.
 *
 * See the same notes for BombBombApiHelperWithApiKeyAuthFallback for how authorization data uses
 * an OAuth token or API Key.
 */
export class VesApiHelper extends ApiHelper {
  constructor(vesData, authToken) {
    super(vesData.baseUrl, {
      headers: new Headers({
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json; charset=utf-8',
      }),
    });
    this.vesData = vesData;
    this.authToken = authToken;
  }

  /**
   * @param {string} videoId
   * @param {string} containerFormat
   * @returns {Promise<{recordingETag: string, recordingId: string}>}
   */
  async createVideoRecording(videoId, containerFormat) {
    try {
      const methodDebug = debug.extend('createVideoRecording');
      const params = {
        videoId,
        containerFormat,
        env: this.vesData.requestEnv,
        startingRawChunkNumber: 1,
        endingRawChunkNumber: DurableRecording.PRE_SIGNED_URL_BATCH_SIZE,
      };
      methodDebug('create params', params);
      const payload = JSON.stringify(params);

      const response = await this.post(VesData.createVideoRecordingPath, {
        body: payload,
      });
      methodDebug('create response', response);

      const { recordingId, recordingETag } = response;
      if (!recordingId) {
        throw new Error('No recordingId returned from create video recording API');
      }
      if (!recordingETag) {
        throw new Error('No recordingETag returned from create video recording API');
      }

      return response;
    } catch (error) {
      logError('Error creating video recording', error);
      throw error;
    }
  }

  /**
   * @param {string} videoId
   * @param {string} containerFormat
   * @param {string} recordingId
   * @param {string} recordingETag
   * @param {number} startingRawChunkNumber
   * @param {number} endingRawChunkNumber
   * @returns {Promise<{rawChunkUploadPreSignedUrl: string}>}
   */
  async createRawChunkUploadPreSignedUrls(
    videoId,
    containerFormat,
    recordingId,
    recordingETag,
    startingRawChunkNumber,
    endingRawChunkNumber,
  ) {
    try {
      const methodDebug = debug.extend('createRawChunkUploadPreSignedUrls');
      const params = {
        videoId,
        containerFormat,
        env: this.vesData.requestEnv,
        recordingId,
        recordingETag,
        startingRawChunkNumber,
        endingRawChunkNumber,
      };
      methodDebug('params', params);
      const payload = JSON.stringify(params);

      const response = await this.post(VesData.createRawChunkUploadPreSignedUrlsPath, {
        body: payload,
      });
      methodDebug('response', response);

      const { rawChunkUploadPreSignedUrls } = response;
      if (!rawChunkUploadPreSignedUrls) {
        throw new Error('No pre-signed URLs returned from API endpoint');
      }

      return response;
    } catch (error) {
      logError('Error creating raw chunk upload pre-signed URLs', error);
      throw error;
    }
  }

  /**
   *
   * @param videoId
   * @param containerFormat
   * @param recordingId
   * @param recordingETag
   * @param completedRawChunks
   * @returns {Promise<{videoUrl: string}>}
   */
  async completeVideoRecording(
    videoId,
    containerFormat,
    recordingId,
    recordingETag,
    completedRawChunks,
  ) {
    try {
      const methodDebug = debug.extend('completeVideoRecording');
      const params = {
        videoId,
        containerFormat,
        env: this.vesData.requestEnv,
        recordingId,
        recordingETag,
        completedRawChunks,
      };
      methodDebug('complete params', params);
      const payload = JSON.stringify(params);

      const response = await this.post(VesData.completeVideoRecordingPath, {
        body: payload,
      });
      methodDebug('complete response', response);

      const { videoUrl } = response;
      if (!videoUrl) {
        throw new Error('No videoUrl returned from complete video recording API');
      }

      return response;
    } catch (error) {
      logError('Error completing video recording', error);
      throw error;
    }
  }
}
